/**
 * Shared PV1–PV4 payout policy helpers (venues.payout_policy).
 * Used by Payout Rules Builder, Venue payouts admin, and Payout Capture.
 *
 * `getSignerSlots` / defaults: keep in sync with `mobile/payoutPolicyUtils.js` and
 * `backend/src/utils/payoutSignerSlots.js`.
 * The TIERS list and PV1-PV4 defaults are drift-checked by
 * `backend/tests/modules/16-payout-policy-sync.guard.test.js`.
 *
 * Role identifiers are `staff_roles.role_key` values; `patron` is a reserved
 * sentinel for the player. Display labels come from the staff_roles lookup,
 * not from this file.
 */

export const TIERS = ['PV1', 'PV2', 'PV3', 'PV4']

export const defaultPolicyForTier = (tierId) => {
  const base = (enabled, kyc, occ, minA, maxA, cash, pin, sig, signers, issuer) =>
    ({
      enabled,
      kycRequired: kyc,
      occupationRequired: occ,
      minAmount: minA,
      maxAmount: maxA,
      cashDispensed: cash,
      pinEntryByRoleId: pin,
      signaturesRequired: sig,
      signerRoleIds: signers,
      authorizationChain: [],
      issuerRoleId: issuer
    })
  switch (tierId) {
    case 'PV1':
      return base(true, false, false, 0, 2000, 'CRT_OR_CASH', null, 0, ['patron', 'cashier'], 'cashier')
    case 'PV2':
      return base(true, false, false, 2000, 5000, 'CASH_ONLY', null, 0, ['patron', 'cashier', 'duty_manager'], 'duty_manager')
    case 'PV3':
      return base(true, true, false, 5000, 10000, 'NO_CASH', 'duty_manager', 0, ['patron', 'duty_manager', 'gaming_manager'], 'gaming_manager')
    case 'PV4':
    default:
      return base(true, true, false, 10000, null, 'NO_CASH', 'duty_manager', 2, ['patron', 'duty_manager', 'gaming_manager', 'venue_manager'], 'gaming_manager')
  }
}

export const ensureVenuePolicy = (venue) => {
  const policy = { ...(venue.payoutPolicy || {}) }
  TIERS.forEach((t) => {
    const def = defaultPolicyForTier(t)
    const existing = policy[t] || {}
    const merged = { ...def }
    for (const [k, v] of Object.entries(existing)) {
      if (v !== undefined) merged[k] = v
    }
    if (Array.isArray(merged.authorizationChain) && merged.authorizationChain.length > 0) {
      if (!merged.signerRoleIds?.length) {
        merged.signerRoleIds = [...merged.authorizationChain]
      }
      merged.authorizationChain = []
    }
    policy[t] = merged
  })
  return { ...venue, payoutPolicy: policy }
}

/** API venue row → merged payoutPolicy object */
export function payoutPolicyFromVenueRow(v) {
  let policy = {}
  if (v.payout_policy) {
    try {
      policy = typeof v.payout_policy === 'object' ? v.payout_policy : JSON.parse(v.payout_policy)
    } catch (_) {
      policy = {}
    }
  }
  return ensureVenuePolicy({ payoutPolicy: policy }).payoutPolicy
}

/**
 * Pick PV tier from amount using venue policy (first matching bracket PV1→PV4).
 * @param {number} amount
 * @param {Record<string, object>} fullPolicy — output of payoutPolicyFromVenueRow
 */
export function resolveTierFromAmount(amount, fullPolicy) {
  const n = Number(amount)
  if (Number.isNaN(n) || n < 0) return 'PV1'
  for (const tid of TIERS) {
    const p = fullPolicy[tid]
    if (!p?.enabled) continue
    const min = Number(p.minAmount ?? 0)
    const max = p.maxAmount == null || p.maxAmount === '' ? null : Number(p.maxAmount)
    if (n < min) continue
    if (max != null && n > max) continue
    return tid
  }
  return 'PV1'
}

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '∞'
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(amount)
}

export const formatTierRange = (min, max) => {
  if (max === null || max === undefined || max === '') return `${formatCurrency(min)}+`
  return `${formatCurrency(min)} – ${formatCurrency(max)}`
}

/** Patron sentinel role id + label (the player is not a staff_roles entry). */
export const PATRON_ROLE_ID = 'patron'
export const PATRON_LABEL = 'Player'

function sortPatronFirst(roleIds) {
  const idx = roleIds.indexOf(PATRON_ROLE_ID)
  if (idx <= 0) return roleIds
  const next = [...roleIds]
  next.splice(idx, 1)
  next.unshift(PATRON_ROLE_ID)
  return next
}

function collectSignatureRoleIds(policy) {
  if (!policy || typeof policy !== 'object') return []
  const out = []
  const seen = new Set()
  const push = (id) => {
    if (id == null || id === '' || seen.has(id)) return
    seen.add(id)
    out.push(id)
  }
  for (const id of policy.signerRoleIds || []) push(id)
  if (policy.issuerRoleId) push(policy.issuerRoleId)
  return out
}

/**
 * Signature slots for payout capture UIs — same rules as mobile `getSignerSlots`.
 * `roleId` is a staff_roles.role_key (or the `patron` sentinel); `label`
 * defaults to the role_key — callers resolve the human label from staff_roles.
 * @param {object} policy — single PV tier object
 * @returns {{ key: string, roleId: string, label: string }[]}
 */
export function getSignerSlots(policy) {
  if (!policy) return []
  const fromConfiguredRoles = collectSignatureRoleIds(policy)
  if (fromConfiguredRoles.length > 0) {
    return sortPatronFirst(fromConfiguredRoles).slice(0, 8).map((roleId, i) => ({
      key: `slot-${i}-${roleId}`,
      roleId,
      label: roleId,
    }))
  }
  const n = Math.min(Math.max(0, policy.signaturesRequired ?? 0), 5)
  if (n === 0) return []
  const roleIdsFromPolicy = Array.isArray(policy.signerRoleIds) ? policy.signerRoleIds.filter(Boolean) : []
  const defaults = ['cashier', 'duty_manager', 'gaming_manager', 'venue_manager']
  const slots = []
  for (let i = 0; i < n; i++) {
    const roleId = roleIdsFromPolicy[i] ?? defaults[i] ?? 'cashier'
    slots.push({
      key: `slot-${i}-${roleId}`,
      roleId,
      label: roleId,
    })
  }
  return slots
}
